import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Plus, Edit, Trash2, Copy, Eye, EyeOff,
  ChevronUp, ChevronDown, Layout, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Per-page section/block manager.
 * Lets admins add, edit, duplicate, reorder, hide/show or detach blocks for a single page.
 * Backed by /api/cms/pages/{pageId}/sections, /reorder, /detach + /api/cms/sections CRUD.
 */
export function PageBuilder({ pageId, pageSlug, api, onEditBlock, refreshKey }) {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const loadPage = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/cms/admin/pages/${pageId}`);
      setPage(data);
    } catch (err) {
      console.error('Failed to load page:', err);
      toast.error('Failed to load page blocks');
    } finally {
      setLoading(false);
    }
  }, [api, pageId]);

  useEffect(() => {
    if (pageId) loadPage();
  }, [pageId, refreshKey, loadPage]);

  const blocks = page?.section_data || [];

  const moveBlock = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[idx], next[j]] = [next[j], next[idx]];
    setPage(p => ({ ...p, section_data: next }));
    try {
      await api.post(`/api/cms/pages/${pageId}/reorder`, {
        section_ids: next.map(b => b.id),
      });
    } catch (err) {
      toast.error('Failed to reorder');
      loadPage();
    }
  };

  const toggleVisible = async (block) => {
    setBusyId(block.id);
    try {
      await api.put(`/api/cms/sections/${block.id}`, {
        ...block,
        is_visible: !block.is_visible,
        id: block.id,
      });
      loadPage();
    } catch (err) {
      toast.error('Failed to update visibility');
    } finally {
      setBusyId(null);
    }
  };

  const duplicateBlock = async (block) => {
    setBusyId(block.id);
    try {
      const { data } = await api.post(`/api/cms/sections/${block.id}/duplicate`);
      // Attach the clone to this page
      await api.post(`/api/cms/pages/${pageId}/reorder`, {
        section_ids: [...blocks.map(b => b.id), data.section.id],
      });
      // After reorder endpoint, also need to add to page.sections
      // The reorder endpoint sets page.sections to the array we send, so this works.
      loadPage();
      toast.success('Block duplicated');
    } catch (err) {
      console.error(err);
      toast.error('Failed to duplicate');
    } finally {
      setBusyId(null);
    }
  };

  const detachBlock = async (block) => {
    if (!window.confirm(`Remove this block from "${page.title}"? The block itself will remain in the library.`)) return;
    setBusyId(block.id);
    try {
      await api.post(`/api/cms/pages/${pageId}/detach/${block.id}`);
      toast.success('Block removed from page');
      loadPage();
    } catch (err) {
      toast.error('Failed to remove');
    } finally {
      setBusyId(null);
    }
  };

  const deleteBlock = async (block) => {
    if (!window.confirm('Permanently delete this block? This cannot be undone.')) return;
    setBusyId(block.id);
    try {
      await api.delete(`/api/cms/sections/${block.id}`);
      toast.success('Block deleted');
      loadPage();
    } catch (err) {
      toast.error('Failed to delete');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="w-6 h-6 animate-spin text-[#00E5FF]" />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="page-builder">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-outfit text-lg text-white">Page Blocks</h3>
          <p className="text-[#5C7599] text-xs">Drag handles, edit inline, hide/show, duplicate or remove blocks for <code className="text-[#00E5FF]">/{pageSlug}</code>.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild size="sm" variant="ghost" className="text-[#A0B4CC]">
            <a href={`/page/${pageSlug}`} target="_blank" rel="noopener noreferrer" data-testid="open-page-preview">
              <Eye className="w-4 h-4 mr-1" /> Preview
            </a>
          </Button>
          <Button
            size="sm"
            onClick={() => onEditBlock(null, { pageId })}
            className="btn-primary"
            data-testid="add-block-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Block
          </Button>
        </div>
      </div>

      {blocks.length === 0 ? (
        <Card className="bg-[#041A3B] border-white/10 border-dashed">
          <CardContent className="py-10 text-center">
            <Layout className="w-10 h-10 text-[#5C7599] mx-auto mb-3" />
            <p className="text-[#A0B4CC] mb-4">No blocks yet — start building your page.</p>
            <Button
              size="sm"
              onClick={() => onEditBlock(null, { pageId })}
              className="btn-primary"
              data-testid="add-first-block-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add your first block
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {blocks.map((block, idx) => (
            <BlockRow
              key={block.id}
              block={block}
              idx={idx}
              total={blocks.length}
              busy={busyId === block.id}
              onMove={moveBlock}
              onEdit={() => onEditBlock(block, { pageId })}
              onToggleVisible={toggleVisible}
              onDuplicate={duplicateBlock}
              onDetach={detachBlock}
              onDelete={deleteBlock}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function BlockRow({ block, idx, total, busy, onMove, onEdit, onToggleVisible, onDuplicate, onDetach, onDelete }) {
  const summary = block.title || block.subtitle || (block.content && stripTags(block.content).slice(0, 80))
    || (block.html && 'Raw HTML') || (block.image_url && 'Image') || (block.video_url && 'Video')
    || `Untitled ${block.section_type}`;

  return (
    <Card className="bg-[#041A3B] border-white/10" data-testid={`block-row-${idx}`}>
      <CardContent className="p-3">
        <div className="flex items-center gap-3">
          {/* Reorder */}
          <div className="flex flex-col">
            <button
              type="button"
              onClick={() => onMove(idx, -1)}
              disabled={idx === 0 || busy}
              className="p-1 text-[#5C7599] hover:text-white hover:bg-white/10 rounded disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid={`move-up-${idx}`}
            >
              <ChevronUp className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => onMove(idx, 1)}
              disabled={idx === total - 1 || busy}
              className="p-1 text-[#5C7599] hover:text-white hover:bg-white/10 rounded disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid={`move-down-${idx}`}
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>

          {/* Order pill */}
          <div className="w-7 h-7 rounded bg-[#000B18] border border-white/10 flex items-center justify-center text-[#A0B4CC] text-xs font-mono">
            {idx + 1}
          </div>

          {/* Content summary */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <Badge className="bg-[#007AFF]/20 text-[#007AFF] text-[10px] uppercase tracking-wide">
                {block.section_type}
              </Badge>
              {block.is_visible === false && (
                <Badge className="bg-[#5C7599]/30 text-[#A0B4CC] text-[10px]">Hidden</Badge>
              )}
              {block.cta_label && (
                <Badge className="bg-[#00E5FF]/15 text-[#00E5FF] text-[10px]">CTA</Badge>
              )}
              {block.background_image && (
                <Badge className="bg-white/10 text-[#A0B4CC] text-[10px]">BG image</Badge>
              )}
            </div>
            <p className="text-white text-sm font-medium truncate">{summary}</p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1">
            <IconBtn title={block.is_visible !== false ? 'Hide' : 'Show'} onClick={() => onToggleVisible(block)} disabled={busy} testId={`toggle-visible-${idx}`}>
              {block.is_visible !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            </IconBtn>
            <IconBtn title="Edit" onClick={onEdit} disabled={busy} testId={`edit-block-${idx}`}>
              <Edit className="w-4 h-4 text-[#00E5FF]" />
            </IconBtn>
            <IconBtn title="Duplicate" onClick={() => onDuplicate(block)} disabled={busy} testId={`duplicate-block-${idx}`}>
              <Copy className="w-4 h-4" />
            </IconBtn>
            <IconBtn title="Remove from page" onClick={() => onDetach(block)} disabled={busy} testId={`detach-block-${idx}`}>
              <span className="text-xs">↗</span>
            </IconBtn>
            <IconBtn title="Delete block" onClick={() => onDelete(block)} disabled={busy} testId={`delete-block-${idx}`}>
              <Trash2 className="w-4 h-4 text-[#FF1744]" />
            </IconBtn>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function IconBtn({ children, onClick, disabled, title, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testId}
      className="p-1.5 rounded text-[#A0B4CC] hover:bg-white/10 hover:text-white transition-colors disabled:opacity-40"
    >
      {children}
    </button>
  );
}

function stripTags(html) {
  if (!html) return '';
  return String(html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

export default PageBuilder;
